import React, { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { fr } from 'date-fns/locale';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import dayjs from 'shared/helpers/dayjs';
import useClickOutside from 'shared/hooks/useClickOutside';
import { Arrow, General, Time } from 'icons/untitledui';
import { calendarPopperSx } from 'dashboard/styles/Filters.style';
import * as Styled from 'shiftNotes/styles/MissingInfoCard.style';

type BinaryVariantProps = {
  variant: 'binary';
  value?: boolean | null;
  onChange: (value: boolean) => void;
};

type DateVariantProps = {
  variant: 'date';
  value: Date | null;
  onChange: (value: Date | null) => void;
  placeholder?: string;
  dateFormat?: string;
};

type TextVariantProps = {
  variant: 'text';
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
};

type ActiveVariantProps =
  | BinaryVariantProps
  | DateVariantProps
  | TextVariantProps;

type MissingInfoCardProps = {
  question: string;
  currentStep: number;
  totalSteps: number;
  onPrev: () => void;
  onNext: () => void;
  canGoPrev?: boolean;
  canGoNext?: boolean;
  errorMessage?: string | null;
} & ActiveVariantProps;

const DEFAULT_DATE_FORMAT = 'DD/MM/YYYY';

function ProgressBar({
  currentStep,
  totalSteps,
}: {
  currentStep: number;
  totalSteps: number;
}): JSX.Element {
  const segments = Array.from({ length: totalSteps }, (_, i) => i + 1);
  return (
    <Styled.ProgressTrack
      role="progressbar"
      aria-valuemin={1}
      aria-valuemax={totalSteps}
      aria-valuenow={currentStep}
    >
      {segments.map((step) => (
        <Styled.ProgressSegment key={step}>
          <Styled.ProgressFill $filled={step <= currentStep} />
        </Styled.ProgressSegment>
      ))}
    </Styled.ProgressTrack>
  );
}

function BinaryInput({
  value,
  onChange,
}: Omit<BinaryVariantProps, 'variant'>): JSX.Element {
  const { t } = useTranslation('pageShiftNotes');
  return (
    <Styled.BinaryRow>
      <Styled.BinaryButton
        $size="small"
        $selected={value === true}
        type="button"
        onClick={() => onChange(true)}
        aria-pressed={value === true}
      >
        {t('missingInfoCard.binary.yes')}
      </Styled.BinaryButton>
      <Styled.BinaryButton
        $size="small"
        $selected={value === false}
        type="button"
        onClick={() => onChange(false)}
        aria-pressed={value === false}
      >
        {t('missingInfoCard.binary.no')}
      </Styled.BinaryButton>
    </Styled.BinaryRow>
  );
}

function DateInput({
  value,
  onChange,
  placeholder,
  dateFormat,
}: Omit<DateVariantProps, 'variant'>): JSX.Element {
  const { t, i18n } = useTranslation('pageShiftNotes');
  const locale = i18n.language === 'fr' ? fr : undefined;
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLButtonElement>(null);
  const popperRef = useRef<HTMLDivElement>(null);
  const format = dateFormat ?? DEFAULT_DATE_FORMAT;

  const displayValue = value
    ? dayjs(value).format(format)
    : placeholder ?? t('missingInfoCard.date.placeholder');

  const handleClose = useCallback(() => setOpen(false), []);

  useClickOutside([anchorRef, popperRef], handleClose, open);

  return (
    <>
      <Styled.DatePill
        ref={anchorRef}
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        aria-label={t('missingInfoCard.date.selectDate')}
      >
        <Styled.DatePillValue $isPlaceholder={!value}>
          {displayValue}
        </Styled.DatePillValue>
        <Styled.DatePillIcon aria-hidden="true">
          <Time.Calendar />
        </Styled.DatePillIcon>
      </Styled.DatePill>
      <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={locale}>
        <DesktopDatePicker
          value={value}
          onChange={(next) => {
            onChange(next);
            setOpen(false);
          }}
          open={open}
          onClose={handleClose}
          slots={{
            leftArrowIcon: Arrow.ChevronLeft,
            rightArrowIcon: Arrow.ChevronRight,
          }}
          slotProps={{
            textField: {
              sx: {
                width: 0,
                height: 0,
                overflow: 'hidden',
                '& .MuiOutlinedInput-root': {
                  display: 'none',
                },
              },
            },
            popper: {
              ref: popperRef,
              anchorEl: anchorRef.current,
              placement: 'bottom-start',
              sx: calendarPopperSx,
            },
          }}
        />
      </LocalizationProvider>
    </>
  );
}

function TextInput({
  value,
  onChange,
  placeholder,
}: Omit<TextVariantProps, 'variant'>): JSX.Element {
  const { t } = useTranslation('pageShiftNotes');
  return (
    <Styled.TextArea
      value={value}
      onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
        onChange(e.target.value)
      }
      placeholder={placeholder ?? t('missingInfoCard.text.placeholder')}
      aria-label={t('missingInfoCard.text.ariaLabel')}
    />
  );
}

function renderVariantInput(
  variantProps: ActiveVariantProps,
  onAutoAdvance?: () => void
): JSX.Element {
  if (variantProps.variant === 'binary') {
    const { value, onChange } = variantProps;
    const handleChange = (next: boolean) => {
      onChange(next);
      onAutoAdvance?.();
    };
    return <BinaryInput value={value} onChange={handleChange} />;
  }
  if (variantProps.variant === 'date') {
    const { value, onChange, placeholder, dateFormat } = variantProps;
    const handleChange = (next: Date | null) => {
      onChange(next);
      if (next) onAutoAdvance?.();
    };
    return (
      <DateInput
        value={value}
        onChange={handleChange}
        placeholder={placeholder}
        dateFormat={dateFormat}
      />
    );
  }
  const { value, onChange, placeholder } = variantProps;
  return (
    <TextInput value={value} onChange={onChange} placeholder={placeholder} />
  );
}

export default function MissingInfoCard(
  props: MissingInfoCardProps
): JSX.Element {
  const { t } = useTranslation('pageShiftNotes');

  const {
    question,
    currentStep,
    totalSteps,
    onPrev,
    onNext,
    canGoPrev,
    canGoNext,
    errorMessage,
  } = props;

  const isLastStep = currentStep >= totalSteps;
  const prevDisabled = canGoPrev === false || currentStep <= 1;
  const nextDisabled = !canGoNext;

  const handleAutoAdvance = () => {
    if (!isLastStep) {
      onNext();
    }
  };

  return (
    <Styled.Wrapper>
      <Styled.Card>
        <Styled.Header>
          <General.HelpCircle aria-hidden="true" />
          <Styled.HeaderLabel>{t('missingInfoCard.title')}</Styled.HeaderLabel>
        </Styled.Header>
        <Styled.Question>{question}</Styled.Question>
        {renderVariantInput(props, handleAutoAdvance)}
        {errorMessage && (
          <Styled.ErrorMessage role="alert">{errorMessage}</Styled.ErrorMessage>
        )}
        <Styled.Pagination>
          <Styled.NavButton
            $size="small"
            type="button"
            onClick={onPrev}
            disabled={prevDisabled}
            aria-label={t('missingInfoCard.nav.previous')}
          >
            <Arrow.ArrowLeft />
          </Styled.NavButton>
          <ProgressBar currentStep={currentStep} totalSteps={totalSteps} />
          {isLastStep ? (
            <Styled.SubmitButton
              $size="small"
              type="button"
              onClick={onNext}
              disabled={nextDisabled}
              aria-label={t('missingInfoCard.nav.submit')}
            >
              <General.Check />
            </Styled.SubmitButton>
          ) : (
            <Styled.NavButton
              $size="small"
              type="button"
              onClick={onNext}
              disabled={nextDisabled}
              aria-label={t('missingInfoCard.nav.next')}
            >
              <Arrow.ArrowRight />
            </Styled.NavButton>
          )}
        </Styled.Pagination>
      </Styled.Card>
      <Styled.SuggestionHint as="div" $size="medium" stretch>
        {t('missingInfoCard.suggestionHint')}
      </Styled.SuggestionHint>
    </Styled.Wrapper>
  );
}
